<template>
  <div>
    <v-row noGutters>
      <BaseSidebar />
    </v-row>
    <v-row>
      <v-col :cols="cols">
        <TableRemote
          v-if="hasPermission('training.video.view')"
          hideDefaultFooter
          :headers="tableHeaders"
          :items="items"
          :pagination="pagination"
          :title="$t('training.pageName')"
          :iconHeader="hasPermission('training.video.add') ? 'mdi-plus' : null"
          disableFiltering
          :isSearchable="false"
          :itemActions="itemActions"
          :isCustomIcon="true"
          @iconAction="addTrainingInfos"
        >
          <template
            #buttonsLineHeader
          >
            <v-col
              cols="12"
              sm="6"
              md="4"
              class="offset-sm-6 offset-md-8"
            >
              <VSelect
                :label="$t('forms.labels.category')"
                :items="videoCategories"
                itemValue="key"
                itemText="name"
                :value="videoCategories[0]"
                @change="getItemsByVideoCategory"
              />
            </v-col>
          </template>

          <template v-slot:[`item.url`]="{ item }">
            <img
              :src="item.preview"
              :width="'200px'"
              @click="getUrl(item.url)"
            >
          </template>
        </TableRemote>

        <NoPermissionAlert
          v-else
          :message="$t('permissions.error')"
        />
      </v-col>
      <v-col
        v-if="url"
        cols="5"
        class="youtube"
      >
        <youtube
          ref="youtube"
          :videoId="videoId"
          :playerVars="playerVars"
        />
      </v-col>
    </v-row>
  </div>
</template>

<script>
import Vue from 'vue';
import { mapGetters, mapActions } from 'vuex';
import TrainingForm from '@/components/training/TrainingForm';
import TableRemote from '@/components/shared/content-views/VTableRemote';
import TourGuideMixin from '@/mixins/TourGuideMixin';
import ConfirmForm from '@/components/shared/widgets/ConfirmForm.vue';
import NoPermissionAlert from '@/components/shared/NoPermissionAlert';
import BaseSidebar from '@/components/shared/navigation/BaseSidebar.vue';


export default {
    components: {
        TableRemote,
        NoPermissionAlert,
        BaseSidebar,
    },
    mixins: [TourGuideMixin],
    data() {
        return {
            videoId: '',
            trainings: [],
            videoCategory: '',
            items: [],
            pagination: {},
            url: '',
            cols: '',
            playerVars: {
                autoplay: 1,
            },
            videoCategories: [],
            preview: '',
        };
    },
    computed: {
        ...mapActions({
            setLang: 'setLang',
        }),
        ...mapGetters([
            'lang',
            'hasPermission',
            'userLang',
        ]),
        itemActions() {
            const actions = [
                { name: this.$t('forms.btn.edit'), icon: 'edit' },
                { name: this.$t('forms.btn.delete'), icon: 'delete' },
            ];
            return actions;
        },
        tableHeaders() {
            return [
                'url',
                'title',
                'category',
                'duration',
                'actions',
            ].map(header => ({
                text: header !== 'url' && header !== 'actions' ? this.$t(`training.labels.${header}`) : '',
                value: header,
            }));
        },
    },
    watch: {
        lang() {
            this.getVideoCategories();
            this.videoCategory = this.$t('forms.btn.selectAll');
            this.getItems();
        },
        url(val) {
            if (val) {
                this.videoId = this.$youtube.getIdFromUrl(val);
                this.cols = 7;
            }
        },
    },
    mounted() {
        this.cols = 12;
        this.videoCategory = this.$t('forms.btn.selectAll');
        this.getVideoCategories();
        this.getItems();
    },
    methods: {
        async getItems() {
            const { data } = await this.$rq.getTrainings();
            this.trainings = data;
            this.getItemsByVideoCategory(this.videoCategory);
        },
        addTrainingInfos(elt, action) {
            if (elt === 'mdi-plus') {
                this.$root.$emit('showModal', {
                    title: this.$t('training.add').toUpperCase(),
                    component: Vue.component('TrainingForm', TrainingForm),
                    buttonTitle: 'confirm',
                    onConfirm: requestPayload => this.addTrainingRequest(requestPayload),
                });
            } else if (action.icon === 'edit') {
                this.editTrainingInfos(elt);
            } else if (action.icon === 'delete') {
                this.removeTrainingInfos(elt);
            }
        },
        editTrainingInfos(item) {
            this.$root.$emit('showModal', {
                title: this.$t('training.edit').toUpperCase(),
                component: Vue.component('TrainingForm', TrainingForm),
                buttonTitle: 'confirm',
                payload: { payload: item },
                onConfirm: requestPayload => this.editTrainingRequest(requestPayload),
            });
        },
        removeTrainingInfos(item) {
            this.$root.$emit('showModal', {
                title: this.$t('training.remove').toUpperCase(),
                component: Vue.component('ConfirmForm', ConfirmForm),
                buttonTitle: 'confirm',
                payload: { msg: this.$t('training.msg.remove') },
                isForm: false,
                onConfirm: () => this.removeTrainingRequest(item.id),
            });
        },
        async addTrainingRequest(requestPayload) {
            const payload = this.$utils.removeEmptyNullOrUndefined(requestPayload, true);
            await this.$rq.addTraining(payload);
            this.$toasted.global.toast_success({
                message: this.$t('msg.trainingCreated', { name: requestPayload.title }),
            });
            await this.getItems();
        },
        async editTrainingRequest(requestPayload) {
            await this.$rq.editTraining(requestPayload);
            this.$toasted.global.toast_success({
                message: this.$t('msg.trainingEdited', { name: requestPayload.title }),
            });
            await this.getItems();
        },
        async removeTrainingRequest(requestPayload) {
            const payload = this.$utils.removeEmptyNullOrUndefined(requestPayload, true);
            await this.$rq.removeTraining(payload);
            this.$toasted.global.toast_success({
                message: this.$t('msg.trainingRemoved', { name: requestPayload.title }),
            });
            await this.getItems();
        },
        getUrl(url) {
            this.url = url;
        },
        getUrlForPreview(url) {
            if (url) {
                this.videoId = this.$youtube.getIdFromUrl(url);
                this.preview = `https://img.youtube.com/vi/${this.videoId}/sddefault.jpg`;
            }
            return this.preview;
        },
        async getVideoCategories() {
            this.videoCategories = [];
            const { data } = await this.$rq.getVideoCategories();
            this.videoCategories[0] = this.$t('forms.btn.selectAll');
            await this.videoCategories.push(...data.map(videoCategory => ({ name: videoCategory.name[this.userLang], key: videoCategory.id })));
        },
        getItemsByVideoCategory(val) {
            if (val && val === this.$t('forms.btn.selectAll')) {
                this.items = this.trainings.videos.map(
                    video => ({ ...video, category: video.category.name[this.userLang], preview: this.getUrlForPreview(video.url) }),
                );
            } else {
                this.items = this.trainings.videos.filter(video => video.category.id === val).map(
                    video => ({ ...video, category: video.category.name[this.userLang], preview: this.getUrlForPreview(video.url) }),
                );
            }
        },
        onClickAction(item, action) {
            if (action) {
                switch (action.icon) {
                    case 'delete':
                        this.handleRemove(item);
                        break;
                    case 'edit':
                        this.onClickEdit(item);
                        break;
                    default:
                }
            }
        },
    },
};
</script>

<style lang="scss" scoped>
::v-deep .text-start{
    width: 200px;
}
::v-deep .youtube{
    margin-top: 210px;
}
</style>
